import React, { Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { ThemeProvider } from './contexts/ThemeContext';
import AppRoutes from './AppRoutes';
import { Layout } from './components/Layout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/account/Login';
import Register from './pages/account/Register';
import SelectOrganisation from './pages/account/SelectOrganisation';
import AcceptInvite from './pages/account/AcceptInvite';
import ForgotPassword from './pages/account/ForgotPassword';
import ResetPassword from './pages/account/ResetPassword';

function AuthGated() {
  const { isAuthenticated, requiresOrgSelection } = useAuth();

  if (requiresOrgSelection) {
    return <SelectOrganisation />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Suspense fallback={
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      }>
        <Routes>
          {AppRoutes.map((route, index) => {
            const { element, ...rest } = route;
            return <Route key={index} {...rest} element={element} />;
          })}
        </Routes>
      </Suspense>
    </Layout>
  );
}

function ProtectedApp() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<AuthGated />} />
    </Routes>
  );
}

function ThemedApp() {
  const { user } = useAuth();
  const interfaceLayout = user?.interfaceLayout || 'Compact';

  return (
    <ThemeProvider interfaceLayout={interfaceLayout}>
      <ProtectedApp />
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ThemedApp />
    </AuthProvider>
  );
}
